import React, { useState } from 'react';
import { Button } from '../../../../../catalyst-components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../catalyst-components/table';
import { Id } from '../../../../../convex/_generated/dataModel';
import { AdminRoutes, SkillAssistant } from '../../../../../shared/models';
import { useMutation } from 'convex/react';
import { api } from '../../../../../convex/_generated/api';
import { ConfirmationDialog } from '../../../../../catalyst-components/confirmation-dialog';
import { useNavigate } from 'react-router-dom';
import { useToastContext } from '../../../../../shared/components/ToastContainer';

interface SkillAssistantTableProps {
    skill_id: Id<"skills">;
    assistants: SkillAssistant[];
}

export const SkillAssistantTable: React.FC<SkillAssistantTableProps> = ({
    skill_id,
    assistants,
}) => {
    const [confirmDelete, setConfirmDelete] = useState<{
        open: boolean;
        assistant?: { 
          _id: Id<"skill_assistants">; 
          name: string;
        };
    }>({ open: false });
    const [movingAssistants, setMovingAssistants] = useState<Set<string>>(new Set());
    const [isReordering, setIsReordering] = useState(false);
    const moveAssistantUp = useMutation(api.skill_assistants.moveUp);
    const moveAssistantDown = useMutation(api.skill_assistants.moveDown);
    const deleteAssistant = useMutation(api.skill_assistants.remove);
    const reorderAllAssistants = useMutation(api.skill_assistants.reorderAllAssistants);
    const navigate = useNavigate();
    const { showToast } = useToastContext();

    const openAssistant = (assistant_id: Id<"assistants">) => {
        navigate(`${AdminRoutes.Assistants}/${assistant_id}`);
    }

    const handleReorderAssistants = async () => {
        if (isReordering) return; // Prevent multiple clicks
        
        setIsReordering(true);
        try {
            await reorderAllAssistants({ skill_id });
            showToast('info', 'Success', 'Skill assistant sequences have been reordered successfully.');
        } catch (error) {
            showToast('danger', 'Error', 'Failed to reorder skill assistant sequences.');
        } finally {
            setIsReordering(false);
        }
    }

    const handleMoveUp = async (assistant: SkillAssistant) => {
        if (movingAssistants.has(assistant._id)) return; // Prevent multiple clicks
        
        setMovingAssistants(prev => new Set(prev).add(assistant._id));
        try {
            await moveAssistantUp({
                skill_id,
                current_sequence: assistant.sequence,
            });
            showToast('info', 'Success', 'Assistant moved up successfully.');
        } catch (error) {
            showToast('danger', 'Error', 'Failed to move assistant up.');
        } finally {
            setMovingAssistants(prev => {
                const newSet = new Set(prev);
                newSet.delete(assistant._id);
                return newSet;
            });
        }
    }

    const handleMoveDown = async (assistant: SkillAssistant) => {
        if (movingAssistants.has(assistant._id)) return; // Prevent multiple clicks
        
        setMovingAssistants(prev => new Set(prev).add(assistant._id));
        try {
            await moveAssistantDown({
                skill_id,
                current_sequence: assistant.sequence,
            });
            showToast('info', 'Success', 'Assistant moved down successfully.');
        } catch (error) {
            showToast('danger', 'Error', 'Failed to move assistant down.');
        } finally {
            setMovingAssistants(prev => {
                const newSet = new Set(prev);
                newSet.delete(assistant._id);
                return newSet;
            });
        }
    }

    return (
        <>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Skill Assistants</h3>
                <Button
                    color="light"
                    onClick={handleReorderAssistants}
                    title="Fix any duplicate or corrupted sequence numbers"
                    disabled={isReordering}
                >
                    {isReordering ? 'Reordering...' : 'Fix Sequences'}
                </Button>
            </div>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>Actions</TableHeader>
                        <TableHeader>Open</TableHeader>
                        <TableHeader>Sequence</TableHeader>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Assistant</TableHeader>
                        <TableHeader>Type</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {assistants?.sort((a, b) => a.sequence - b.sequence).map((assistant) => (
                        <TableRow key={assistant._id}>
                            <TableCell>
                                <div className="flex gap-2">
                                    <div className="flex gap-1">
                                        <Button
                                            color="light"
                                            className="w-8"
                                            disabled={assistant.sequence === 1 || movingAssistants.has(assistant._id)}
                                            onClick={() => handleMoveUp(assistant)}
                                        >
                                            {movingAssistants.has(assistant._id) ? '⋯' : '↑'}
                                        </Button>
                                        <Button
                                            color="light"
                                            className="w-8"
                                            disabled={assistant.sequence === assistants.length || movingAssistants.has(assistant._id)}
                                            onClick={() => handleMoveDown(assistant)}
                                        >
                                            {movingAssistants.has(assistant._id) ? '⋯' : '↓'}
                                        </Button>
                                    </div>
                                    <Button
                                        color="red"
                                        className="w-8"
                                        onClick={() => setConfirmDelete({ 
                                            open: true, 
                                            assistant: { 
                                              _id: assistant._id, 
                                              name: assistant.name 
                                            }
                                        })}
                                    >
                                        ×
                                    </Button>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Button
                                    color="light"
                                    className="w-16"
                                    onClick={() => openAssistant(assistant.assistant_id)}
                                >
                                    Open
                                </Button>
                            </TableCell>
                            <TableCell>{assistant.sequence}</TableCell>
                            <TableCell>{assistant.assistantDisplayName || 'No Assistant'}</TableCell>
                            <TableCell>{assistant.name}</TableCell>
                            <TableCell>{assistant.assistantType || 'Unknown'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <ConfirmationDialog
                open={confirmDelete.open}
                onClose={() => setConfirmDelete({ open: false })}
                isCritical={true}
                onConfirm={async () => {
                    if (!confirmDelete.assistant) return;
                    try {
                        await deleteAssistant({
                            id: confirmDelete.assistant._id,
                        });
                        showToast('info', 'Success', `Skill assistant "${confirmDelete.assistant.name}" deleted successfully.`);
                    } catch (error) {
                        showToast('danger', 'Error', `Failed to delete skill assistant "${confirmDelete.assistant.name}".`);
                    }
                }}
                title="Delete Skill Assistant"
                description={`Are you sure you want to delete "${confirmDelete.assistant?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
            />
        </>
    );
};
